import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import WarnOnce from '../components/WarnOnce';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { errorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const WARN_MSG = "C'est une application communautaire donc les informations que vous allez ajouter dans ce tournoi seront accessibles par tout le monde. Nous vous invitons à prendre le temps de relire et vérifier vos informations. Ajouter le lien pour s'inscrire et les différents formats du tournoi facilitera le partage de ce tournoi à d'autres membres de la communauté.";

const SURFACE_OPTIONS = [
  ['sable',  'Sable'],
  ['indoor', 'Salle'],
  ['herbe',  'Herbe'],
  ['neige',  'Neige'],
];
const SURFACE_MAP = { sable: 'beach', indoor: 'gymnase', herbe: 'green', neige: 'neige' };
// Reverse map : surface stockée (backend) → clé du wizard
const SURFACE_MAP_REV = { beach: 'sable', gymnase: 'indoor', green: 'herbe', neige: 'neige' };

const FORMATS = ['2x2', '3x3', '4x4', '6x6'];
const LEVEL_OPTIONS = ['Loisir', 'Départemental', 'Régional', 'N3', 'N2', 'N1', 'Élite'];
const GENDERS = [
  { value: 'masculin', label: 'Masculin' },
  { value: 'feminin',  label: 'Féminin'  },
  { value: 'mix',      label: 'Mixte'    },
];

let catSeq = 0;
// Draft = catégorie en cours d'édition, ajoutée à la liste récap via "Ajouter une catégorie" puis réinitialisée.
const emptyDraft = () => ({
  format: '3x3', gender: 'mix', levelOpen: true, level: 'Loisir',
  meetingTime: '10:00', registrationCloseTime: '09:30', price: '', priceMode: 'player', ageMax: '', maxTeams: '',
});
function fmtT(t) { if (!t) return ''; const [h, m] = t.split(':'); return `${parseInt(h, 10)}h${m}`; }
// Résumé compact d'une catégorie dans la liste récap, ex: "3x3 Mixte · Régional · 10h00 · 12€/joueur"
function summarizeCategory(c) {
  const genderLabel = GENDERS.find(g => g.value === c.gender)?.label || c.gender;
  const level = c.levelOpen ? 'Ouvert à tous' : `${c.level} max`;
  const priceLabel = c.price ? `${c.price}€/${c.priceMode === 'team' ? 'équipe' : 'joueur'}` : 'Gratuit';
  const age = c.ageMax ? ` · ${c.ageMax} ans max` : '';
  return { main: `${c.format} ${genderLabel} · ${level}${age}`, sub: `${fmtT(c.meetingTime)} · ${priceLabel}` };
}

function CheckIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5 9-11"/>
    </svg>
  );
}
function FoodIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10h16M5 10v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8"/>
      <path d="M8 10V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4"/>
      <path d="M12 14v3"/>
    </svg>
  );
}
function LinkIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  );
}
function PinIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/>
      <circle cx="12" cy="9" r="2.5"/>
    </svg>
  );
}
function CalIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="5" width="17" height="15" rx="2"/>
      <path d="M3.5 10h17M8 3v4M16 3v4"/>
    </svg>
  );
}
function ClockIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5"/><path d="M12 7v5l3 2"/>
    </svg>
  );
}

const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' };
const SUB_LBL = { ...MONO, fontSize: 9, color: 'var(--text-3)' };

function CTField({ label, placeholder, value, onChange, type = 'text', icon, error, optionalBadge }) {
  return (
    <div className="card col" style={{
      padding: '4px 10px', gap: 1, marginBottom: 0,
      borderColor: error ? '#C62828' : undefined,
      background: error ? '#FFF8F8' : undefined,
    }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ ...MONO, fontSize: 9, color: 'var(--text-3)' }}>{label}</span>
        {optionalBadge && <span style={{ ...MONO, fontSize: 8, color: 'var(--text-3)', opacity: 0.6 }}>Optionnel</span>}
      </div>
      <div className="row gap-2">
        {icon && <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>{icon}</span>}
        <input
          type={type} value={value} onChange={onChange} placeholder={placeholder}
          style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0,
            color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }}
        />
      </div>
      {typeof error === 'string' && error && (
        <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600, paddingBottom: 2 }}>{error}</span>
      )}
    </div>
  );
}

const REGISTRATION_MODES = [
  ['spike', 'Dans Spike (paiement sur place)'],
  ['helloasso', 'HelloAsso'],
  ['weezevent', 'Weezevent'],
  ['other', 'Autre plateforme'],
];

// Select label+badge sur le même gabarit visuel que CTField (mais pour un <select>).
function CTFieldSelect({ label, value, onChange, options }) {
  return (
    <div className="card col" style={{ padding: '4px 10px', gap: 1, marginBottom: 0 }}>
      <span style={{ ...MONO, fontSize: 9, color: 'var(--text-3)' }}>{label}</span>
      <select value={value} onChange={onChange} style={{
        flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0,
        color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', padding: '4px 0',
      }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function CTInline({ placeholder, value, onChange, type = 'text', icon, min }) {
  return (
    <div className="row gap-2" style={{
      flex: 1, padding: '5px 8px', borderRadius: 10,
      background: 'var(--ink-3)', alignItems: 'center', minWidth: 0,
    }}>
      {icon && <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>{icon}</span>}
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder} min={min}
        style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0,
          color: 'var(--text)', fontSize: 12, fontFamily: 'inherit' }}
      />
    </div>
  );
}

function IOSToggle({ value, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!value)} className="row gap-2"
      style={{ alignItems: 'center', background: 'none', border: 'none', padding: 0 }}>
      <span style={{
        width: 30, height: 18, borderRadius: 9, padding: 2,
        background: value ? 'var(--sun)' : 'var(--ink-4)',
        display: 'inline-flex', justifyContent: value ? 'flex-end' : 'flex-start',
        transition: 'background .2s, justify-content .2s', flexShrink: 0,
      }}>
        <span style={{ width: 14, height: 14, borderRadius: '50%', background: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{label}</span>
    </button>
  );
}

function CTSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={onChange} style={{
      flex: 1, minWidth: 0, padding: '7px 8px', borderRadius: 8, border: 'none',
      background: 'var(--ink-3)', color: 'var(--text)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

// Toggle segmenté orange (ancienne version) — actif = fond var(--sun).
function PriceModeToggle({ value, onChange }) {
  return (
    <div className="row" style={{ background: 'var(--ink-3)', borderRadius: 999, padding: 2 }}>
      {[['player', '/ joueur'], ['team', '/ équipe']].map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} style={{
          padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, border: 'none',
          background: value === k ? 'var(--sun)' : 'transparent',
          color: value === k ? 'var(--ink)' : 'var(--text-3)',
        }}>{l}</button>
      ))}
    </div>
  );
}

// CT-catégories : éditeur du draft en cours (format+genre+niveau max+prix/mode+heures+âge max), aligné sur l'orga.
// Validé via "Ajouter une catégorie" → ajoute un résumé à la liste récap et réinitialise le draft.
function CategoryDraftEditor({ draft, errors, onSet, onSetValue, onSetChecked, onToggleAgeMax, onAdd }) {
  return (
    <div className="card col" style={{ padding: 10, gap: 8, marginBottom: 0 }}>
      <div className="row gap-2">
        <CTSelect value={draft.format} onChange={onSet('format')} options={FORMATS.map(f => [f, f])} />
        <CTSelect value={draft.gender} onChange={onSet('gender')} options={GENDERS.map(g => [g.value, g.label])} />
      </div>
      <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
        <input type="checkbox" checked={draft.levelOpen} onChange={onSetChecked('levelOpen')} style={{ width: 16, height: 16 }} />
        Ouvert à tous niveaux
      </label>
      {!draft.levelOpen && (
        <div className="col" style={{ gap: 4 }}>
          <div style={SUB_LBL}>Niveau max</div>
          <CTSelect value={draft.level} onChange={onSet('level')} options={LEVEL_OPTIONS.map(l => [l, l])} />
        </div>
      )}
      {errors.level && <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>{errors.level}</span>}
      <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
        <input type="checkbox" checked={draft.ageMax !== ''} onChange={onToggleAgeMax} style={{ width: 16, height: 16 }} />
        Limite d'âge
      </label>
      {draft.ageMax !== '' && <CTInline type="number" min="0" placeholder="Âge max" value={draft.ageMax} onChange={onSet('ageMax')} />}
      <div className="row gap-2" style={{ alignItems: 'center' }}>
        <CTInline type="number" min="0" step="0.5" placeholder="Prix €" value={draft.price} onChange={onSet('price')} />
        <PriceModeToggle value={draft.priceMode} onChange={(v) => onSetValue('priceMode', v)} />
      </div>
      {errors.price && <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>{errors.price}</span>}
      <div className="row gap-2">
        <div style={{ flex: 1 }}>
          <div style={{ ...SUB_LBL, marginBottom: 4 }}>RDV</div>
          <CTInline type="time" value={draft.meetingTime} onChange={onSet('meetingTime')} icon={<ClockIcon />} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...SUB_LBL, marginBottom: 4 }}>Fin émargement</div>
          <CTInline type="time" value={draft.registrationCloseTime} onChange={onSet('registrationCloseTime')} icon={<ClockIcon />} />
        </div>
      </div>
      {(errors.meetingTime || errors.registrationCloseTime) &&
        <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>{errors.meetingTime || errors.registrationCloseTime}</span>}
      <CTInline type="number" min="2" placeholder="Équipes max (optionnel)" value={draft.maxTeams} onChange={onSet('maxTeams')} />
      <button type="button" className="button-secondary btn-sm" onClick={onAdd}>+ Ajouter une catégorie</button>
    </div>
  );
}

// CTN1: section-title margin augmenté de 20px→28px pour aérer
const SECTION_TITLE_STYLE = { margin: '28px 0 8px' };

export default function CreateTournamentWizard({ user }) {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const isEdit = !!tournamentId;
  const token = localStorage.getItem('token');

  const [name, setName]             = useState('');
  const [date, setDate]             = useState('');
  const [multiDay, setMultiDay]     = useState(false);
  const [endDate, setEndDate]       = useState('');
  const [locationName, setLocationName]   = useState('');
  const [locationCity, setLocationCity]   = useState('');
  const [surface, setSurface]       = useState('sable');
  const [categories, setCategories] = useState([]); // liste récap (catégories déjà ajoutées)
  const [draft, setDraft]           = useState(emptyDraft()); // catégorie en cours d'édition
  const [draftErrors, setDraftErrors] = useState({});
  const [externalUrl, setExternalUrl] = useState('');
  const [registrationMode, setRegistrationMode] = useState('spike');
  const [food, setFood]             = useState(false);

  const [similarTournament, setSimilarTournament] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Mode édition : pré-remplir le formulaire depuis le tournoi existant ──
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API_URL}/api/tournaments/${tournamentId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data: t }) => {
        setName(t.name || '');
        setDate(t.date || '');
        if (t.endDate) { setMultiDay(true); setEndDate(t.endDate); }
        // location stockée = "lieu précis, ville" → on resépare
        const parts = (t.location || '').split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length > 1) {
          setLocationCity(parts[parts.length - 1]);
          setLocationName(parts.slice(0, -1).join(', '));
        } else {
          setLocationName(parts[0] || '');
        }
        setSurface(SURFACE_MAP_REV[t.surface] || 'sable');
        if (Array.isArray(t.categories) && t.categories.length) {
          setCategories(t.categories.map(c => ({
            key: ++catSeq, format: c.format, gender: c.gender,
            // rétro-compat : anciennes catégories stockées en plage (levelMin/levelMax) → on reprend le plafond (levelMax).
            levelOpen: c.level == null && c.levelMin == null && c.levelMax == null,
            level: c.level || c.levelMax || 'Loisir',
            meetingTime: c.meetingTime || '10:00', registrationCloseTime: c.registrationCloseTime || '09:30',
            price: c.price != null ? String(c.price) : '', priceMode: c.priceMode === 'team' ? 'team' : 'player',
            ageMax: c.ageMax != null ? String(c.ageMax) : '', maxTeams: c.maxTeams != null ? String(c.maxTeams) : '',
          })));
        } else {
          // Rétro-compat : tournoi pré-migration → une catégorie par combinaison format×genre, ouverte à tous (ne bloque personne).
          const fmts = t.playerFormats?.length ? t.playerFormats : (t.playerFormat ? [t.playerFormat] : ['3x3']);
          const gens = t.genders?.length ? t.genders : (t.gender ? [t.gender] : ['mix']);
          const cats = fmts.flatMap(f => gens.map(g => ({
            key: ++catSeq, format: f, gender: g, levelOpen: true, level: 'Loisir',
            meetingTime: t.time || '10:00', registrationCloseTime: t.registrationCloseTime || '09:30',
            price: t.price ? String(t.price) : '', priceMode: t.priceMode === 'team' ? 'team' : 'player',
            ageMax: '', maxTeams: '',
          })));
          setCategories(cats);
        }
        setExternalUrl(t.externalUrl || '');
        setRegistrationMode(t.registrationMode || 'other'); // ponytail: absence = 'other' (rétro-compat)
        setFood(Boolean(t.food));
      })
      .catch(() => setError("Impossible de charger le tournoi à modifier."));
  }, [isEdit, tournamentId]); // eslint-disable-line

  const toggleMultiDay = (v) => {
    setMultiDay(v);
    if (!v) setEndDate('');
  };

  const buildLocation = () =>
    [locationName, locationCity].filter(Boolean).join(', ');

  // ── Draft catégorie (éditeur unique) ──
  const setDraftValue = (field, value) => { setDraft(d => ({ ...d, [field]: value })); setDraftErrors(e => { const n = { ...e }; delete n[field]; return n; }); };
  const setDraftField = (field) => (e) => setDraftValue(field, e.target.value);
  const setDraftChecked = (field) => (e) => setDraftValue(field, e.target.checked);
  const toggleDraftAgeMax = (e) => setDraftValue('ageMax', e.target.checked ? '18' : '');

  const validateDraft = (d) => {
    const errs = {};
    if (!d.levelOpen && !d.level)        errs.level = 'Choisis un niveau max ou coche « Ouvert à tous ».';
    if (d.price === '' || isNaN(parseFloat(d.price))) errs.price = 'Le prix est requis (0 si gratuit).';
    if (!d.meetingTime)                  errs.meetingTime = "L'heure de RDV est requise.";
    if (!d.registrationCloseTime)        errs.registrationCloseTime = "L'heure de fin d'émargement est requise.";
    return errs;
  };
  const addCategoryToList = () => {
    const errs = validateDraft(draft);
    if (Object.keys(errs).length > 0) { setDraftErrors(errs); return; }
    setCategories(cs => [...cs, { ...draft, key: ++catSeq }]);
    setDraft(emptyDraft());
    setDraftErrors({});
    clearErr('categories');
  };
  const removeCategory = (key) => setCategories(cs => cs.filter(c => c.key !== key));
  // Modifier : recharge la catégorie dans l'éditeur et la retire de la liste (ré-ajoutée au clic sur « Ajouter »).
  const editCategory = (c) => { setDraft({ ...c }); setDraftErrors({}); setCategories(cs => cs.filter(x => x.key !== c.key)); };

  const validate = () => {
    const errs = {};
    if (!name.trim())               errs.name           = 'Donne un nom à ton tournoi.';
    if (!date)                      errs.date           = 'La date est obligatoire.';
    if (multiDay && !endDate)       errs.endDate        = 'Indique la date de fin.';
    if (multiDay && endDate && endDate <= date) errs.endDate = 'La date de fin doit être après le début.';
    if (!locationName.trim())       errs.locationName   = 'Précise le lieu (salle, plage, gymnase…).';
    if (!locationCity.trim())       errs.locationCity   = 'Choisis une ville dans la liste.';
    if (categories.length === 0)    errs.categories     = 'Ajoute au moins une catégorie.';
    if (registrationMode !== 'spike' && !externalUrl.trim())
      errs.externalUrl = "Le lien d'inscription officielle est requis.";
    return errs;
  };

  const doSave = async () => {
    setLoading(true);
    const payload = {
      name: name.trim(), date,
      time: categories[0]?.meetingTime || '10:00', // dérivé par le backend depuis les catégories ; gardé pour compat PUT
      endDate: multiDay ? endDate : null,
      scheduleDetails: null, // CTN2: champ supprimé
      city: locationCity,
      location: buildLocation(),
      categories: categories.map(c => ({
        format: c.format, gender: c.gender,
        level: c.levelOpen ? null : c.level,
        meetingTime: c.meetingTime || null, registrationCloseTime: c.registrationCloseTime || null,
        price: parseFloat(c.price) || 0, priceMode: c.priceMode,
        ageMax: parseInt(c.ageMax) || null, maxTeams: parseInt(c.maxTeams) || null,
      })),
      surface: SURFACE_MAP[surface] || null,
      externalUrl: externalUrl.trim() || null,
      registrationMode,
      food,
      visibility: 'public', // CTN4: toujours public
    };
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/tournaments/${tournamentId}`, payload,
          { headers: { Authorization: `Bearer ${token}` } });
        navigate(`/tournaments/${tournamentId}`);
      } else {
        await axios.post(`${API_URL}/api/tournaments`, { groupId: null, ...payload },
          { headers: { Authorization: `Bearer ${token}` } });
        navigate('/dashboard');
      }
    } catch (err) {
      setError(errorMessage(err, isEdit ? "La modification a échoué. Vérifie les informations et réessaie."
        : "La création a échoué. Vérifie les informations et réessaie."));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    // En édition, pas de détection de doublon (on modifie un tournoi existant).
    if (!isEdit) {
      try {
        const res = await axios.get(
          `${API_URL}/api/tournaments/similar?date=${encodeURIComponent(date)}&location=${encodeURIComponent(buildLocation())}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.length > 0) { setSimilarTournament(res.data[0]); return; }
      } catch { /* ignore */ }
    }
    await doSave();
  };

  const clearErr = (key) => fieldErrors[key] && setFieldErrors(p => { const n = {...p}; delete n[key]; return n; });

  return (
    <>
      {!isEdit && <WarnOnce storageKey="warn_create_tournament" message={WARN_MSG} />}

      {/* ── Header ── */}
      <div style={{ padding: '0 20px 8px', flexShrink: 0, paddingTop: 42,
        borderBottom: '1px solid var(--line)', background: 'var(--ink)' }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="display" style={{ fontSize: 17 }}>{isEdit ? 'Modifier le tournoi' : 'Ajouter un tournoi'}</div>
          <button type="button" onClick={() => navigate(-1)}
            style={{ color: 'var(--text-3)', fontSize: 12, fontWeight: 600,
              background: 'none', border: 'none', padding: '8px 4px' }}>
            Annuler
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="page-content" style={{ paddingBottom: 120 }}>
        <form onSubmit={handleSubmit} noValidate>

          {error && (
            <div style={{ margin: '16px 0 0', padding: '10px 14px', borderRadius: 10,
              background: 'rgba(229,80,62,0.1)', color: '#E5503E',
              border: '1px solid rgba(229,80,62,0.25)', fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          )}

          {/* ── Section Infos ── */}
          <div className="section-title" style={SECTION_TITLE_STYLE}><h3 style={{ fontSize: 10, margin: 0 }}>Infos</h3></div>
          <div className="section col gap-2">

            <CTField label="Nom du tournoi *" placeholder="ex: Pampelonne Sunset Cup"
              value={name} onChange={e => { setName(e.target.value); clearErr('name'); }}
              error={fieldErrors.name} />

            {/* Ville — recherche BAN (désambiguïsée par code postal) */}
            <div className="card col" style={{ padding: '4px 10px', gap: 1, marginBottom: 0, overflow: 'visible',
              borderColor: fieldErrors.locationCity ? '#C62828' : undefined, background: fieldErrors.locationCity ? '#FFF8F8' : undefined }}>
              <span style={{ ...MONO, fontSize: 9, color: 'var(--text-3)' }}>Ville *</span>
              <div className="row gap-2" style={{ alignItems: 'center' }}>
                <span style={{ color: 'var(--text-3)', flexShrink: 0 }}><PinIcon /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AddressAutocomplete mode="city" value={locationCity} placeholder="ex: Saint-Tropez"
                    inputStyle={{ width: '100%', background: 'transparent', border: 0, outline: 'none', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', padding: 0 }}
                    onChange={(v) => { setLocationCity(v); clearErr('locationCity'); }}
                    onSelect={(c) => { setLocationCity(c.label); setLocationName(''); clearErr('locationCity'); }} />
                </div>
              </div>
              {typeof fieldErrors.locationCity === 'string' && <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>{fieldErrors.locationCity}</span>}
            </div>

            {/* Lieu précis — filtré par la ville choisie */}
            <div className="card col" style={{ padding: '4px 10px', gap: 1, marginBottom: 0, overflow: 'visible',
              borderColor: fieldErrors.locationName ? '#C62828' : undefined, background: fieldErrors.locationName ? '#FFF8F8' : undefined }}>
              <span style={{ ...MONO, fontSize: 9, color: 'var(--text-3)' }}>Lieu précis *</span>
              <div className="row gap-2" style={{ alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AddressAutocomplete mode="address" value={locationName} city={locationCity || undefined}
                    placeholder={locationCity ? 'ex: Plage de Pampelonne' : "Choisis d'abord la ville"}
                    inputStyle={{ width: '100%', background: 'transparent', border: 0, outline: 'none', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', padding: 0 }}
                    onChange={(v) => { setLocationName(v); clearErr('locationName'); }}
                    onSelect={(a) => { setLocationName(a.name || a.label); clearErr('locationName'); }} />
                </div>
              </div>
              {typeof fieldErrors.locationName === 'string' && <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>{fieldErrors.locationName}</span>}
            </div>

            <CTFieldSelect label="Où se passe l'inscription officielle ?"
              value={registrationMode} onChange={e => setRegistrationMode(e.target.value)}
              options={REGISTRATION_MODES} />

            {registrationMode !== 'spike' && (
              <CTField label="Lien d'inscription officielle *" placeholder="https://mon-tournoi.fr/inscription"
                value={externalUrl} onChange={e => { setExternalUrl(e.target.value); clearErr('externalUrl'); }}
                type="url" icon={<LinkIcon />} error={fieldErrors.externalUrl} />
            )}

            {/* Dates */}
            <div className="card col" style={{ padding: '5px 10px', gap: 4, marginBottom: 0 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span style={SUB_LBL}>Dates *</span>
                <IOSToggle value={multiDay} onChange={toggleMultiDay} label="Plusieurs jours" />
              </div>
              {!multiDay ? (
                <div className="row gap-2">
                  <CTInline type="date" placeholder="Date" value={date}
                    onChange={e => { setDate(e.target.value); clearErr('date'); }} icon={<CalIcon />} />
                </div>
              ) : (
                /* CTN2 : multi-jours sans scheduleDetails, heure de RDV/fermeture inscr. propres à chaque catégorie */
                <div className="row gap-2">
                  <CTInline type="date" placeholder="Début" value={date}
                    onChange={e => { setDate(e.target.value); clearErr('date'); }} icon={<CalIcon />} />
                  <CTInline type="date" placeholder="Fin" value={endDate} min={date || undefined}
                    onChange={e => { setEndDate(e.target.value); clearErr('endDate'); }} icon={<CalIcon />} />
                </div>
              )}
              {fieldErrors.date && (
                <span style={{ fontSize: 10, color: '#C62828' }}>Date requise</span>
              )}
              {fieldErrors.endDate && <span style={{ fontSize: 10, color: '#C62828' }}>
                {endDate && endDate <= date ? 'Date fin > début' : 'Date de fin requise'}
              </span>}
            </div>
          </div>

          {/* ── Section Surface ── */}
          <div className="section-title" style={SECTION_TITLE_STYLE}><h3 style={{ fontSize: 10, margin: 0 }}>Surface</h3></div>
          <div className="section">
            <div className="row gap-2">
              {SURFACE_OPTIONS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setSurface(k)} style={{
                  flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: surface === k ? 'var(--text)' : 'var(--ink-3)',
                  color: surface === k ? 'var(--ink)' : 'var(--text-2)',
                  border: 'none', transition: 'background 0.15s',
                }}>{l}</button>
              ))}
            </div>
          </div>

          {/* ── Section Catégories ── */}
          <div className="section-title" style={SECTION_TITLE_STYLE}>
            <h3 style={{ fontSize: 10, margin: 0 }}>Catégories</h3>
            <span style={{ ...MONO, fontSize: 9, color: 'var(--text-3)' }}>{categories.length} sél.</span>
          </div>
          <div className="section col" style={{ gap: 10 }}>
            <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
              Chaque catégorie est un tournoi simultané : format, genre, niveau, prix/mode et horaires indépendants.
              Remplis le formulaire puis clique sur « Ajouter une catégorie ».
            </span>
            {categories.map(c => { const s = summarizeCategory(c); return (
              <div key={c.key} className="card row" style={{ padding: '10px 12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, gap: 8 }}>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.main}</div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.sub}</div>
                </div>
                <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                  <button type="button" aria-label="Modifier" onClick={() => editCategory(c)} style={{
                    width: 30, height: 30, padding: 0, borderRadius: '50%', background: 'var(--ink-3)', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                  </button>
                  <button type="button" aria-label="Supprimer" onClick={() => removeCategory(c.key)} style={{
                    width: 30, height: 30, padding: 0, borderRadius: '50%', background: 'rgba(198,40,40,0.12)', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C62828' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>
                  </button>
                </div>
              </div>
            ); })}
            {fieldErrors.categories && <span style={{ fontSize: 10, color: '#C62828' }}>{fieldErrors.categories}</span>}
            <CategoryDraftEditor draft={draft} errors={draftErrors}
              onSet={setDraftField} onSetValue={setDraftValue}
              onSetChecked={setDraftChecked} onToggleAgeMax={toggleDraftAgeMax}
              onAdd={addCategoryToList} />
          </div>

          {/* ── Section Sur place ── */}
          <div className="section-title" style={SECTION_TITLE_STYLE}><h3 style={{ fontSize: 10, margin: 0 }}>Sur place</h3></div>
          <div className="section">
            <button type="button" onClick={() => setFood(!food)} className="card row gap-3" style={{
              padding: '8px 10px', textAlign: 'left', width: '100%', marginBottom: 0,
              borderColor: food ? 'var(--sun)' : 'var(--line)',
              background: food ? 'rgba(245,166,35,0.08)' : 'var(--ink-2)',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                background: food ? 'rgba(245,166,35,0.18)' : 'var(--ink-3)',
                color: food ? 'var(--sun)' : 'var(--text-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FoodIcon size={16} />
              </div>
              <div className="col" style={{ flex: 1 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>Restauration sur place</span>
                <span style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>
                  Buvette, snack ou food-trucks pendant le tournoi.
                </span>
              </div>
              <span style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                border: '2px solid ' + (food ? 'var(--sun)' : 'var(--line-2)'),
                background: food ? 'var(--sun)' : 'transparent',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--ink)',
              }}>
                {food && <CheckIcon size={11} />}
              </span>
            </button>
          </div>

          <div style={{ height: 30 }} />

        </form>
      </div>

      {/* ── Fixed submit button ── */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: 'min(430px, 100vw)', padding: '12px 20px 28px',
        background: 'linear-gradient(to top, var(--ink) 70%, transparent)',
        zIndex: 20,
      }}>
        <button onClick={handleSubmit} disabled={loading}
          className="btn btn-primary" style={{ width: '100%', padding: '13px 20px' }}>
          {loading
            ? (isEdit ? 'Enregistrement…' : 'Publication en cours…')
            : (isEdit ? 'Enregistrer les modifications' : 'Publier le tournoi')}
        </button>
      </div>

      {/* ── Doublon popup ── */}
      {similarTournament && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,40,0.6)', zIndex: 400,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}
          onClick={() => setSimilarTournament(null)}>
          <div style={{ background: 'var(--ink)', borderRadius: 24, padding: '24px 20px', width: '100%',
            maxWidth: 390, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}
            onClick={e => e.stopPropagation()}>
            <div className="display" style={{ fontSize: 20, textAlign: 'center', marginBottom: 6 }}>
              Ce tournoi existe peut-être déjà
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-3)', textAlign: 'center', marginBottom: 16, margin: '0 0 16px' }}>
              Nous avons trouvé un tournoi similaire :
            </p>
            <div className="card" style={{ padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>
                {similarTournament.name}
              </div>
              <div className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>
                {new Date(similarTournament.date + 'T12:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <div className="mono" style={{ color: 'var(--text-3)' }}>{similarTournament.location}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-primary" style={{ width: '100%', padding: '13px' }}
                onClick={() => navigate(`/tournament/${similarTournament.id}`)}>
                Oui, c'est ce tournoi
              </button>
              <button className="btn btn-ghost" style={{ width: '100%', padding: '13px' }}
                onClick={() => { setSimilarTournament(null); doSave(); }}>
                Non, créer un autre tournoi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
